import React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import { StepButton, Modal, CircularProgress } from '@mui/material';
import './Stepper.scss';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../../../reduxService/hooks';
import Dialog from '../../../../../components/Dialog/Dialog';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useStepperState } from './hooks/useStepperState';
import { useStepperNavigation } from './hooks/useStepperNavigation';
import { useProjectSubmission } from './hooks/useProjectSubmission';
import { useStepperErrorHandling } from './hooks/useStepperErrorHandling';

/**
 * HorizontalLinearStepper Component
 *
 * Manages project workflow navigation and calculation execution.
 *
 * Railway Calculation Support:
 * - Backend: Uses C# API via PUT /api/project_results/{id} (hardcoded)
 * - Frontend TypeScript calculation has been deprecated
 */

// Constants for step indices
const STEP_INDICES = {
    PROJECT_INFO: 0,
    PROJECT_TYPE: 1,
    DESIGN_CONDITION: 2,
    RESULTS: 3,
    GEOGRID_DESIGN: 4,
    GEOGRID_RESULTS: 5,
} as const;

export default function HorizontalLinearStepper() {
    const { t } = useTranslation();
    const { id: projectIdFromUrl } = useParams<{ id: string }>();

    const dynamicProject = useAppSelector(state => state.Dynamic.project?.data);
    const outputData = useAppSelector(state => state.Dynamic.results?.data?.outputData);
    const isFormProcessing = useAppSelector(state => state.formControl.isSubmitting);

    // Initialize hooks for state management
    const stepperState = useStepperState({ dynamicProject });
    const { steps, computedActiveStep, isApiLoading, setIsApiLoading, errorDialogContent, setErrorDialogContent, showErrorDialog } = stepperState;

    // Initialize project submission hook
    const projectSubmission = useProjectSubmission({ dynamicProject, showErrorDialog, setIsApiLoading });
    const { handleFormSubmitSuccess, handleGeoGridRunSuccess } = projectSubmission;

    // Initialize navigation hook
    const stepperNavigation = useStepperNavigation({
        steps,
        computedActiveStep,
        dynamicProject,
        outputData,
        projectIdFromUrl,
        isApiLoading,
        isFormProcessing,
        setErrorDialogContent,
        setIsApiLoading,
    });
    const { handleStep, isStepDisabled } = stepperNavigation;

    // Initialize error handling hook
    const stepperErrorHandling = useStepperErrorHandling({
        setErrorDialogContent,
        handleFormSubmitSuccess,
        handleGeoGridRunSuccess,
        setIsApiLoading,
    });
    const { handleDialogCloseOnly } = stepperErrorHandling;

    return (
        <div className="contStepper" data-testid="stepper-container">
            <Box sx={{ width: '100%', padding: '20px 32px 20px 32px' }}>
                <Stepper nonLinear className="stepper" activeStep={computedActiveStep} connector={null}>
                    {steps.map((stepItem, index) => {
                        const disabled = isStepDisabled(index);
                        const isProjectRunStep = index === STEP_INDICES.RESULTS;
                        const isGeoGridRunStep = index === STEP_INDICES.GEOGRID_RESULTS;

                        // Completion logic might need review based on project flow.
                        // This assumes 'sent' means completed for run steps.
                        // And activeStep > index means previous steps are "completed" by being visited.
                        const completed = (isProjectRunStep && dynamicProject?.hasBeenRan) || (isGeoGridRunStep && dynamicProject?.geogridHasBeenRan) || computedActiveStep > index;

                        let icon = undefined;
                        if (isProjectRunStep && !dynamicProject?.hasBeenRan) {
                            icon = <PlayArrowIcon />;
                        } else if (isGeoGridRunStep && !dynamicProject?.geogridHasBeenRan) {
                            icon = <PlayArrowIcon />;
                        }

                        return (
                            <Step key={`step-${index}-${stepItem.label}`} completed={completed} disabled={disabled}>
                                <StepButton
                                    icon={icon}
                                    className="step-button"
                                    color={'inherit'}
                                    onClick={() => handleStep(stepItem, index)}
                                    disabled={disabled} // Explicitly disable the button
                                >
                                    {stepItem.label}
                                </StepButton>
                            </Step>
                        );
                    })}
                </Stepper>
            </Box>
            <Modal open={isApiLoading || isFormProcessing}>
                <CircularProgress size={80} className="loader" />
            </Modal>
            <Dialog
                open={!!errorDialogContent}
                title={t('errors.projectCommit.title', 'Error') || 'Error'}
                onClose={handleDialogCloseOnly}
                actions={[{ text: 'OK', callback: handleDialogCloseOnly, color: 'primary' }]}>
                <div style={{ whiteSpace: 'pre-line', userSelect: 'text' }}>{errorDialogContent}</div>
            </Dialog>
        </div>
    );
}
